import React from "react";
import { DateTime } from "luxon";
import styles from "./RespondUsersPanel.module.css";

interface CadendarUserOption {
  id: string;
  username: string;
  fullname: string;
  tag: "doctor" | "secretary";
}

interface KnownRespondUser {
  respond_user_id: string;
  respond_user_email: string;
  first_seen_at: Date;
  last_seen_at: Date;
  last_phone_from: string | null;
  last_forwarded_from: string | null;
  link_id: number | null;
  linked_doctor_id: string | null;
  linked_secretary_id: string | null;
  linked_doctor_fullname: string | null;
  linked_doctor_username: string | null;
  linked_secretary_fullname: string | null;
  linked_secretary_username: string | null;
}

interface RespondUsersPanelProps {
  knownRespondUsers: KnownRespondUser[];
  cadendarUsers: CadendarUserOption[];
  onLink: (respondUserId: string, cadendarUserId: string) => void;
  onUnlink: (respondUserId: string) => void;
  onCreateAndLink: (options: {
    respondUserId: string;
    username: string;
    fullname: string;
    messagename: string;
    password: string;
  }) => void;
  isMutating: boolean;
}

interface CreateCadendarUserDraft {
  username: string;
  fullname: string;
  messagename: string;
  password: string;
  confirmPassword: string;
}

function getCreateValidationItems(draft: CreateCadendarUserDraft) {
  return [
    {
      label: "Le username doit contenir au moins 4 caractères.",
      isValid: draft.username.length >= 4,
    },
    {
      label: "Le nom complet doit contenir au moins 4 caractères.",
      isValid: draft.fullname.length >= 4,
    },
    {
      label: "Le nom message doit contenir au moins 4 caractères.",
      isValid: draft.messagename.length >= 4,
    },
    {
      label: "Le mot de passe doit contenir au moins 10 caractères.",
      isValid: draft.password.length >= 10,
    },
    {
      label: "La confirmation du mot de passe doit correspondre.",
      isValid: draft.password.length > 0 && draft.password === draft.confirmPassword,
    },
  ];
}

function formatDate(value: Date) {
  return DateTime.fromJSDate(value).toFormat("dd/MM/yyyy HH:mm");
}

function getLinkedCadendarUserId(user: KnownRespondUser) {
  return user.linked_doctor_id || user.linked_secretary_id || "";
}

function getLinkedCadendarLabel(user: KnownRespondUser) {
  if (user.linked_doctor_id) {
    return `${user.linked_doctor_fullname || user.linked_doctor_username || user.linked_doctor_id} (docteur)`;
  }
  if (user.linked_secretary_id) {
    return `${user.linked_secretary_fullname || user.linked_secretary_username || user.linked_secretary_id} (secrétaire)`;
  }
  return "Non lié";
}

function getDefaultDraft(user: KnownRespondUser): CreateCadendarUserDraft {
  const localPart = user.respond_user_email.split("@")[0] || "respond";
  const normalizedUsername = localPart
    .toLowerCase()
    .replace(/[^a-z0-9._-]/g, "")
    .slice(0, 24);
  const fallbackName = localPart.replace(/[._-]+/g, " ").trim() || "Respond";
  return {
    username: normalizedUsername || "responduser",
    fullname: fallbackName,
    messagename: fallbackName,
    password: "",
    confirmPassword: "",
  };
}

export default function RespondUsersPanel(props: RespondUsersPanelProps) {
  const [selectedCadendarUserByRespondUser, setSelectedCadendarUserByRespondUser] =
    React.useState<Record<string, string>>({});
  const [createDrafts, setCreateDrafts] = React.useState<
    Record<string, CreateCadendarUserDraft>
  >({});

  React.useEffect(() => {
    const nextState = props.knownRespondUsers.reduce<Record<string, string>>(
      (acc, user) => {
        acc[user.respond_user_id] =
          selectedCadendarUserByRespondUser[user.respond_user_id] ||
          getLinkedCadendarUserId(user);
        return acc;
      },
      {},
    );
    setSelectedCadendarUserByRespondUser(nextState);
  }, [props.knownRespondUsers]);

  React.useEffect(() => {
    const nextDrafts = props.knownRespondUsers.reduce<
      Record<string, CreateCadendarUserDraft>
    >((acc, user) => {
      acc[user.respond_user_id] =
        createDrafts[user.respond_user_id] || getDefaultDraft(user);
      return acc;
    }, {});
    setCreateDrafts(nextDrafts);
  }, [props.knownRespondUsers]);

  const updateDraft = (
    respondUserId: string,
    key: keyof CreateCadendarUserDraft,
    value: string,
  ) => {
    setCreateDrafts((prev) => ({
      ...prev,
      [respondUserId]: {
        ...prev[respondUserId],
        [key]: value,
      },
    }));
  };

  return (
    <section className={styles.panel}>
      <div className={styles.headerRow}>
        <h2 className={styles.title}>Migration Respond</h2>
        <div className={styles.count}>
          {props.knownRespondUsers.length} compte(s) Respond détecté(s)
        </div>
      </div>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Compte Respond</th>
            <th>Dernière activité</th>
            <th>Dernier numéro</th>
            <th>Liaison actuelle</th>
            <th>Lier à</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {props.knownRespondUsers.map((user) => {
            const selectedCadendarUserId =
              selectedCadendarUserByRespondUser[user.respond_user_id] || "";
            const draft =
              createDrafts[user.respond_user_id] || getDefaultDraft(user);
            const validationItems = getCreateValidationItems(draft);
            const canCreateAndLink =
              !user.link_id && validationItems.every((item) => item.isValid);
            return (
              <React.Fragment key={user.respond_user_id}>
                <tr>
                  <td>
                    <div className={styles.mainLabel}>{user.respond_user_email}</div>
                    <div className={styles.secondaryLabel}>{user.respond_user_id}</div>
                  </td>
                  <td>
                    <div>{formatDate(new Date(user.last_seen_at))}</div>
                    <div className={styles.secondaryLabel}>
                      Vu pour la première fois le {formatDate(new Date(user.first_seen_at))}
                    </div>
                  </td>
                  <td>
                    <div>{user.last_phone_from || "Numéro masqué ou absent"}</div>
                    <div className={styles.secondaryLabel}>
                      Renvoi: {user.last_forwarded_from || "inconnu"}
                    </div>
                  </td>
                  <td>{getLinkedCadendarLabel(user)}</td>
                  <td>
                    <select
                      value={selectedCadendarUserId}
                      onChange={(event) => {
                        const value = event.target.value;
                        setSelectedCadendarUserByRespondUser((prev) => ({
                          ...prev,
                          [user.respond_user_id]: value,
                        }));
                      }}
                      className={styles.select}
                    >
                      <option value="">Choisir un user Cadendar</option>
                      {props.cadendarUsers.map((cadendarUser) => (
                        <option key={cadendarUser.id} value={cadendarUser.id}>
                          {cadendarUser.fullname} ({cadendarUser.tag === "doctor" ? "docteur" : "secrétaire"})
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <div className={styles.actionRow}>
                      <button
                        disabled={!selectedCadendarUserId || props.isMutating}
                        onClick={() =>
                          props.onLink(user.respond_user_id, selectedCadendarUserId)
                        }
                      >
                        Lier
                      </button>
                      <button
                        disabled={!user.link_id || props.isMutating}
                        onClick={() => props.onUnlink(user.respond_user_id)}
                      >
                        Délier
                      </button>
                    </div>
                  </td>
                </tr>
                {!user.link_id ? (
                  <tr className={styles.createRow}>
                    <td colSpan={6}>
                      <div className={styles.createGrid}>
                        <div>
                          <label className={styles.fieldLabel}>Username</label>
                          <input
                            value={draft.username}
                            onChange={(event) =>
                              updateDraft(
                                user.respond_user_id,
                                "username",
                                event.target.value,
                              )
                            }
                          />
                        </div>
                        <div>
                          <label className={styles.fieldLabel}>Nom complet</label>
                          <input
                            value={draft.fullname}
                            onChange={(event) =>
                              updateDraft(
                                user.respond_user_id,
                                "fullname",
                                event.target.value,
                              )
                            }
                          />
                        </div>
                        <div>
                          <label className={styles.fieldLabel}>Nom message</label>
                          <input
                            value={draft.messagename}
                            onChange={(event) =>
                              updateDraft(
                                user.respond_user_id,
                                "messagename",
                                event.target.value,
                              )
                            }
                          />
                        </div>
                        <div>
                          <label className={styles.fieldLabel}>Mot de passe</label>
                          <input
                            type="password"
                            value={draft.password}
                            onChange={(event) =>
                              updateDraft(
                                user.respond_user_id,
                                "password",
                                event.target.value,
                              )
                            }
                          />
                        </div>
                        <div>
                          <label className={styles.fieldLabel}>Confirmation</label>
                          <input
                            type="password"
                            value={draft.confirmPassword}
                            onChange={(event) =>
                              updateDraft(
                                user.respond_user_id,
                                "confirmPassword",
                                event.target.value,
                              )
                            }
                          />
                        </div>
                        <div className={styles.createActionCell}>
                          <button
                            disabled={!canCreateAndLink || props.isMutating}
                            onClick={() =>
                              props.onCreateAndLink({
                                respondUserId: user.respond_user_id,
                                username: draft.username,
                                fullname: draft.fullname,
                                messagename: draft.messagename,
                                password: draft.password,
                              })
                            }
                          >
                            Créer et lier un user Cadendar
                          </button>
                          <div className={styles.secondaryLabel}>
                            Création en secrétaire par défaut, rôle ajustable ensuite.
                          </div>
                          <div className={styles.validationBox}>
                            <div className={styles.validationTitle}>
                              Validations requises avant la création
                            </div>
                            <ul className={styles.validationList}>
                              {validationItems.map((item) => (
                                <li
                                  key={item.label}
                                  className={
                                    item.isValid
                                      ? styles.validationItemValid
                                      : styles.validationItemInvalid
                                  }
                                >
                                  {item.label}
                                </li>
                              ))}
                            </ul>
                            {!canCreateAndLink ? (
                              <div className={styles.validationSummary}>
                                Le formulaire est incomplet ou invalide. Corrigez les points en rouge pour activer le bouton.
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : null}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}
